import { Container } from "pixi.js";

import { Fitout, Loadout, resolveFitout } from "../../../shared/Fitout";
import { buildDeck, buildFitting } from "../boats/Deck";
import { Game } from "../Game";

/**
 * Pictures of ships and fittings, drawn by the game's own renderer and handed to the DOM as a
 * `<canvas>`.
 *
 * **Why not crop the atlas with CSS.** A boat is a *composition* — a hull, its deck furniture, its
 * two guns and its canvas, each at its own slot, size and rotation — and the game already has one
 * implementation of that in [Deck.ts](../boats/Deck.ts). Cropping would be a second implementation
 * of "how a ship is assembled", and the second one is the one that stops matching the first the
 * day somebody moves a slot. So the shop's cards are the real thing, rendered once and lifted out.
 *
 * **It lives in `ui/` rather than next to `Deck.ts`**, and that is a hard constraint rather than a
 * preference: `Deck.ts` is in the editor's module graph through `Boat.ts`, and this needs
 * `Game.app.renderer`, so putting it there would drag the whole Pixi `Application` into the
 * dashboard's bundle.
 *
 * Extraction is not free — it renders to a texture and reads it back — so nothing here belongs on
 * a per-frame path. The shop calls it when a card is built, which is once.
 */

/** How tall a card's picture is drawn, in device pixels. Enough to read a deck plan at a glance. */
const CARD_PIXELS = 168;

/**
 * One boat as it would actually spawn: the preset with its rig up and a gun in each slot, at
 * **level 0**, which is the size it puts to sea at rather than anything it might grow into.
 *
 * `against` is the length every card in a row is scaled by, so a gallery reads as boats of
 * different sizes rather than as the same boat drawn again. Pass the longest preset in the
 * catalogue and the biggest card fills its box while the rest sit honestly smaller.
 */
export function boatCard(loadout: Loadout, against: number): HTMLCanvasElement | null {
    const fitout = resolveFitout(loadout, 0);
    return deckCard(fitout, against);
}

/** The same, for a fitout somebody already has. Null when the catalogue has no boat to draw. */
export function deckCard(fitout: Fitout, against: number): HTMLCanvasElement | null {
    if (!fitout.preset) return null;

    const deck = buildDeck(fitout);
    return extract(deck.container, CARD_PIXELS / Math.max(1, against));
}

/**
 * One fitting on its own — a gun, at its art's natural size and facing north.
 *
 * A cannon card answers "what kind of thing is this", not "where does it sit", so it is the
 * fitting alone: a gun reads as a gun at this size, where a whole ship with one gun on it does
 * not. Null for art the sheets do not have, same as everywhere else.
 */
export function fittingCard(art: string, tint?: number): HTMLCanvasElement | null {
    const fitting = buildFitting(art);
    if (!fitting) return null;

    // A device is greyscale in the sheet — it exists to be tinted — so a card for one has to be
    // shown in the colour it will fly in. A gun passes no tint and is drawn as it is drawn.
    if (tint !== undefined) fitting.tint = tint;

    // Scaled to its own bounds rather than to a hull length: this is a picture of a thing, so the
    // only sensible size is "as big as the card".
    return extract(fitting, CARD_PIXELS / Math.max(1, fitting.height));
}

/**
 * Renders a scene-graph fragment into a `<canvas>` the DOM can hold, and destroys the fragment.
 *
 * The fragment is only ever scaffolding — nothing else holds a reference to it — so it goes on the
 * way out. Leaving it would be a deck per card kept alive for the life of the page.
 */
function extract(target: Container, resolution: number): HTMLCanvasElement {
    const canvas = Game.app.renderer.extract.canvas({ target, resolution, antialias: true }) as unknown as HTMLCanvasElement;
    canvas.className = "card-art";
    target.destroy({ children: true });

    return canvas;
}
